import { useState, useEffect, useCallback } from 'react';
import { tagsAPI } from '../utils/api';
import { useToast, apiError } from '../components/Toast';

const PRESET_COLORS = ['#2E539F','#5CC3BC','#FFC849','#ef4444','#8b5cf6','#10b981','#f97316'];

/**
 * Drop-in tag picker for a task or project. Usage:
 *   <TagPicker entityType="task" entityId={task.id} />
 */
export default function TagPicker({ entityType, entityId }) {
  const toast = useToast();
  const [allTags, setAllTags]   = useState([]);
  const [attached, setAttached] = useState([]);
  const [open, setOpen]         = useState(false);
  const [newTagName, setNewTagName] = useState('');

  const load = useCallback(async () => {
    const [allRes, attRes] = await Promise.all([
      tagsAPI.getAll(),
      tagsAPI.getFor(entityType, entityId),
    ]);
    setAllTags(allRes.data);
    setAttached(attRes.data);
  }, [entityType, entityId]);

  useEffect(() => { load(); }, [load]);

  const isAttached = (tagId) => attached.some(t => t.id === tagId);

  const toggleTag = async (tag) => {
    try {
      if (isAttached(tag.id)) {
        await tagsAPI.detach({ tag_id: tag.id, entity_type: entityType, entity_id: entityId });
      } else {
        await tagsAPI.attach({ tag_id: tag.id, entity_type: entityType, entity_id: entityId });
      }
    } catch(e) { toast.error(apiError(e, 'Could not change that tag.')); }
    finally { load(); }
  };

  const createAndAttach = async () => {
    if (!newTagName.trim()) return;
    const color = PRESET_COLORS[allTags.length % PRESET_COLORS.length];
    try {
      const r = await tagsAPI.create({ name: newTagName.trim(), color });
      await tagsAPI.attach({ tag_id: r.data.id, entity_type: entityType, entity_id: entityId });
      setNewTagName('');
      toast.success(`Tag "${newTagName.trim()}" added`);
    } catch(e) { toast.error(apiError(e, 'Could not create that tag.')); }
    finally { load(); }
  };

  return (
    <div className="relative">
      <div className="flex items-center gap-1.5 flex-wrap">
        {attached.map(t => (
          <span key={t.id} onClick={() => toggleTag(t)}
            className="text-xs px-2 py-0.5 rounded-full text-white font-medium cursor-pointer hover:opacity-80"
            style={{ background: t.color }} title="Click to remove">
            {t.name} ×
          </span>
        ))}
        <button onClick={() => setOpen(v => !v)}
          className="text-xs px-2 py-0.5 rounded-full border" style={{ borderColor:'var(--border)', color:'var(--text-secondary)' }}>
          + Tag
        </button>
      </div>

      {open && (
        <div className="absolute z-50 mt-2 w-56 rounded-xl shadow-2xl p-2" style={{ background:'var(--surface)', border:'1px solid var(--border)' }}>
          <div className="max-h-40 overflow-y-auto space-y-0.5">
            {allTags.map(t => (
              <label key={t.id} className="flex items-center gap-2 px-2 py-1.5 rounded-lg cursor-pointer hover:bg-[var(--surface-alt)]">
                <input type="checkbox" checked={isAttached(t.id)} onChange={() => toggleTag(t)} />
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: t.color }}/>
                <span className="text-sm" style={{ color:'var(--text)' }}>{t.name}</span>
              </label>
            ))}
          </div>
          <div className="flex gap-1.5 mt-2 pt-2 border-t" style={{ borderColor:'var(--border)' }}>
            <input value={newTagName} onChange={e=>setNewTagName(e.target.value)}
              onKeyDown={e => e.key==='Enter' && createAndAttach()}
              placeholder="New tag..." className="input text-xs py-1 flex-1"/>
            <button onClick={createAndAttach} className="btn-primary text-xs px-2">Add</button>
          </div>
        </div>
      )}
    </div>
  );
}
